import { inject, observer } from "mobx-react";
import React, { useState, useEffect, useMemo } from "react";
import MenuItem from "@material-ui/core/MenuItem";

import NewPackIcon from "@material-ui/icons/CreateNewFolder";
import Icon from "@material-ui/core/Icon";

import styled from "styled-components";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import { CircularProgress, Grid } from "@material-ui/core";
import Paper from "@material-ui/core/Paper";
import InputBase from "@material-ui/core/InputBase";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import PackOptionsIcon from "@material-ui/icons/MoreVert";

import PackName from "app/Header/PackName";
import PackStore, { PackStoreContext } from "app/Pack/PackStore";
import { DIMENSIONS } from "utils/Dimensions";
import COLORS from "scss/colors.module.scss";
import Search from "app/common/components/input/Search";
import * as mui from "@material-ui/icons";
import { snakeCase } from "lodash";
import UserMenu from "../Header/UserMenu";
import { getLogoSrc, getTitle } from "app/common/utils/Logo";
import MyTigersheets from "../Header/MyTigersheets";

import FloatingChatButton from "app/Chat/buttons/FloatingChatButton";
import ChatDialog from "app/Chat/ChatDialog";
import ScheduleCall from "app/OnboardingCall/ScheduleCall";
import StickyBanner from "app/OnboardingCall/StickyBanner";
import ApiClient from "utils/ApiClient";

const BANNER_HEIGHT = 48;
const POPUP_DELAY_MS = 120000;

function Dashboard({ store, userStore, assistantStore, onBoardingStore }) {
  const config = store.appStore.configStore.config;
  const headerTopOffset = onBoardingStore.isScheduleCallBannerOpened ? BANNER_HEIGHT : 0;

  const [isTimeUp, setIsTimeUp] = useState(false);
  const isAdmin = useMemo(() => {
    if (!userStore.user || !userStore.user.isAdminForUsers) {
      // return false if user is not an admin
      return false;
    }

    return true;
  }, [userStore.user]);

  const isOwner = useMemo(() => {
    return Boolean(userStore.user?.isOwner);
  }, [userStore.user]);

  const handleChatButtonClick = () => {
    assistantStore.openChat();
  };

  const handleChatDialogClose = () => {
    if (isAdmin) {
      assistantStore.showAssistantButton();
    } else {
      assistantStore.hideAssistantButton();
    }

    onBoardingStore.closeDialog();
  };

  const handleScheduleCallCloseAll = async () => {
    if(onBoardingStore.scheduleCallDialogOpened) {
      onBoardingStore.closeScheduleCallDialog();
    }
    if(onBoardingStore.isScheduleCallBannerOpened) {
      onBoardingStore.closeScheduleCallBanner();
    }
    if(onBoardingStore.isScheduleCallonMenuOpened) {
      onBoardingStore.closeScheduleCallOnMenu();
    }
  };

  const handleScheduleCallClose = () => {
    onBoardingStore.closeScheduleCallDialog();
  };

  const handleBannerClose = async () => {
    onBoardingStore.closeScheduleCallBanner();
    if(userStore.user.is_onboarding_prompt_visible) {
      onBoardingStore.openScheduleCallOnMenu();
      const resp = await ApiClient.Account.updateOnboardingPrompt({
        user_id: window.appStore.userStore.user.user_id,
        prompt_value: 0,
      });
      if(resp.success) {
        window.appStore.userStore.user.is_onboarding_prompt_visible = false;
      }
    }
  };

  useEffect(() => {
    // Return if user is not an admin
    if (!isAdmin) {
      return;
    }

    // If owner is logging in for first time, show the ScheduleCall dialog first
    if (
      isOwner &&
      !userStore.user.hasCreatedSheets &&
      !userStore.user.hasCreatedPacks
    ) {
      assistantStore.hideAssistantButton();
      onBoardingStore.openDialog();
    } else {
      // Else show floating chat button
      assistantStore.showAssistantButton();
    }
  }, [userStore.user, isAdmin, isOwner]);

  useEffect(() => {
    if (userStore.user?.is_onboarding_prompt_visible) {
      const timer = setTimeout(() => {
        setIsTimeUp(true);
      }, POPUP_DELAY_MS);

      return () => {
        clearTimeout(timer)
      };
    }
  }, [userStore.user?.is_onboarding_prompt_visible]);

  useEffect(() => {
    const shouldShow =
      userStore.user?.is_onboarding_prompt_visible &&
      !onBoardingStore.dialogOpened &&
      !assistantStore.opened;
  
    if (!shouldShow) return;

    if (!onBoardingStore.isScheduleCallBannerOpened) {
      onBoardingStore.openScheduleCallBanner();
    }
  
    if (isTimeUp) {
      onBoardingStore.openScheduleCallDialog();
      setIsTimeUp(false);
    }
  }, [
    isTimeUp,
    userStore.user?.is_onboarding_prompt_visible,
    onBoardingStore.dialogOpened,
    assistantStore.opened
  ]);

  return (
    <>
      <MainDiv>
        <div
          className="sticky-top d-flex"
          style={{
            height: DIMENSIONS.header1,
            backgroundColor: COLORS.THEME_COLOR,
            justifyContent: "center",
            marginTop: headerTopOffset,             
            top: headerTopOffset,
            transition: "all 0.3s ease-in-out",
            zIndex: 1200
          }}
        >
          <img
            src={getLogoSrc()}
            alt={config?.site?.title || "logo"}
            style={{
              height: 28,
              marginLeft: 20,
              marginTop: 8,
            }}
          />
          <h4 style={{ color: "white", padding: 8 }}>{getTitle()}</h4>
          <UserMenuDiv className="align-self-center d-flex">
            <MyTigersheets store={store} />
            <UserMenu packStore={store} isScheduleCallonMenuOpened={onBoardingStore.isScheduleCallonMenuOpened ?? false} />
          </UserMenuDiv>
        </div>
        <CenterAlignedDiv>
          <VerticalTabs store={store} />
        </CenterAlignedDiv>
        {assistantStore.isAssistantButtonVisible && (
          <FloatingChatButton onClick={handleChatButtonClick} />
        )}
      </MainDiv>
      <ScheduleCall
        open={onBoardingStore.scheduleCallDialogOpened}
        onClose={handleScheduleCallClose}
        handleScheduleCallCloseAll={handleScheduleCallCloseAll}
      />
      <ChatDialog
        open={onBoardingStore.dialogOpened}
        onClose={handleChatDialogClose}
      />
      <StickyBanner 
        open={onBoardingStore.isScheduleCallBannerOpened} 
        onClose={handleBannerClose} 
        handleScheduleCallCloseAll={handleScheduleCallCloseAll}
      />
    </>
  );
}

const VerticalTabs = observer(function ({ store }) {
  const [value, setValue] = React.useState(0);
  const [searchText, setSearchText] = React.useState("");
  const packs = store.packs.filter(
    (pack) => !searchText || pack.name.toLowerCase().includes((typeof searchText == "string" ? searchText.toLowerCase() : searchText))
  );
  const recentPacks = [...store.getRecentPackIds()]
    .map((packId) => store.packs.find((pack) => pack.id === packId))
    .filter(
      (pack) =>
        pack && (!searchText || pack.name.toLowerCase().includes(searchText))
    );

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  return (
    <>
      <TabsRoot>
        <TabsStyled
          indicatorColor="primary"
          orientation="vertical"
          variant="scrollable"
          value={value}
          onChange={handleChange}
          aria-label="Vertical tabs"
        >
          <Tab label="All" {...a11yProps(0)} />
          {/* <Tab label="Recent" {...a11yProps(1)} /> */}
        </TabsStyled>
        <TabPanel value={value} index={0} fullWidth>
          <StyledSearch
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search Packs"
          />
          {/* <NewPackMenuItemStyled onClick={() => store.createPack()}>
            <NewPackIcon className="menu_item_icon" />
            New Pack
          </NewPackMenuItemStyled> */}
          <ImportParentGrid container>
            <MenuItemStyled button={false} onClick={store.createPackInDashboard}>
              <Grid
                container
                direction="column"
                justify="center"
                alignItems="center"
              >
                <Grid item>{getIcon({ icon: "create_new_folder" }, true)}</Grid>
                <Grid item>
                  <p>{store.isPackCreating ? "Creating..." : "New Package"}</p>
                </Grid>
              </Grid>
            </MenuItemStyled>
            {/* <Grid container>
              {store.isPacksLoading && <CircularProgress size={30} />} 
              {recentPacks.length === 0 &&
                !store.isPacksLoading &&
                "No Recent Packs"} 
            </Grid> */}
            <ImportPackDiv onClick={store.importExistingTemplate}>
                Import Pack
            </ImportPackDiv>
          </ImportParentGrid>
          <Grid container>
            {store.isPacksLoading && <CircularProgress size={30} />}
            {packs.length === 0 && !store.isPacksLoading && (
              <p style={{ width: "100%", textAlign: "center", opacity: 0.5 }}>
                No Package Found
              </p>
            )}
            <Packs
              packs={recentPacks.reverse()}
              currentPackId={store.currentPackId}
              onClick={store.handlePackItemClick}
              isLoggedInForFirstTimeSession={
                store.appStore?.userStore?.user
                  ?.isLoggedInForFirstTimeSession == true
                  ? true
                  : false
              }
            />
            <Packs
              packs={packs.filter(
                (pack) =>
                  recentPacks.find(
                    (recentPack) => recentPack.id === pack.id
                  ) === undefined
              )}
              currentPackId={store.currentPackId}
              onClick={store.handlePackItemClick}
              isLoggedInForFirstTimeSession={
                store.appStore?.userStore?.user
                  ?.isLoggedInForFirstTimeSession == true
                  ? true
                  : false
              }
            />
          </Grid>
        </TabPanel>
        <TabPanel value={value} index={1}></TabPanel>
      </TabsRoot>
    </>
  );
});

const getIcon = (pack, isIconName = false) => {
  let { icon: iconClasses, id } = pack;
  let packStore,
    packSetting = null;

  if (mui[iconClasses]) {
    isIconName = true;
    iconClasses = snakeCase(iconClasses);
  }

  if (pack.is_admin) {
    if (!pack.store) {
      packStore = new PackStore(window.appStore);
      packStore.id = id;
      packStore.setPackDetails();
      pack.store = packStore;
    }
    packStore = pack.store;
    packSetting = (
      <PackStoreContext.Provider value={packStore}>
        <PackName
          packName={packStore.packName}
          packData={pack}
          changePackName={packStore.handlePackNameChange}
          themeColor={packStore.themeColor}
          changeThemeColor={packStore.handleThemeColorChange}
          icon={packStore.icon}
          changeIcon={packStore.handleIconChange}
          store={packStore}
          isDefaultOpen={
            window.appStore.leftHoverMenuStore.lastCreatedPackId === pack.id
          }
          onClose={(e) => {
            pack.isEditing = false;
            window.appStore.leftHoverMenuStore.clearLastCreatedPackId();
          }}
          isShareHidden
          onOpen={(e) => (pack.isEditing = true)}
          Button={
            <PackOptionsIconButton className="display_on_hover">
              <PackOptionsIcon />
            </PackOptionsIconButton>
          }
        />
      </PackStoreContext.Provider>
    );
  }

  if (iconClasses && !isIconName) {
    return (
      <IconStyled
        className={"menu_item_icon " + iconClasses}
        themeColor={pack.color}
        highlighted={pack.isEditing}
      >
        {packSetting}
      </IconStyled>
    );
  }
  return (
    <IconStyled highlighted={pack.isEditing} themeColor={pack.color}>
      {iconClasses || "folder_open"}
      {packSetting}
    </IconStyled>
  );
};

const Packs = observer(
  ({ packs, onClick, isLoggedInForFirstTimeSession = false }) =>
    packs.map((pack) =>
      isLoggedInForFirstTimeSession == true && pack.name == "System" ? (
        <></>
      ) : (
        <MenuItemStyled
          button={false}
          key={pack.id}
          onClick={() => onClick(pack)}
        >
          <Grid
            container
            direction="column"
            justify="center"
            alignItems="center"
          >
            <Grid item>{getIcon(pack)}</Grid>
            <Grid item>
              <p>{pack.name}</p>
            </Grid>
          </Grid>
        </MenuItemStyled>
      )
    )
);

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
      style={{ width: "80%" }}
    >
      {value === index && (
        <Box p={3}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `vertical-tab-${index}`,
    "aria-controls": `vertical-tabpanel-${index}`,
  };
}

const TabsStyled = styled(Tabs)`
  ${({ theme }) => `
        border-right: 1px solid ${theme.palette.divider}
    `}
`;

const IconStyled = styled(Icon)`
  && {
    height: 60px;
    width: 60px;
    text-align: center;
    color: ${(props) => props.themeColor ?? "rgba(0, 0, 0, 0.87)"};
    cursor: pointer;
    margin: 4px 0px;
    padding: 16px;
    font-size: 60px;
    box-sizing: content-box;
    border-radius: 4px;

    .display_on_hover {
      display: none;
    }

    ${({ theme, highlighted }) => `    
        transition: ${theme.transitions.create(
          ["background-color", "box-shadow"],
          {
            duration: theme.transitions.duration.shortest,
          }
        )};
        &:hover {
            background-color: white;
            box-shadow: ${theme.shadows[1]};
            .display_on_hover {
                display: block;
            }
        }
        ${
          highlighted &&
          `
            background-color: white;
            box-shadow: ${theme.shadows[1]};
            .display_on_hover {
                display: block;
            }
        `
        }
    `};
  }
`;

const MenuItemStyled = styled(MenuItem)`
  && {
    white-space: nowrap;
    text-overflow: ellipsis;
    width: 136px;
    overflow: hidden;
    /* font-size: 12px; */
    & p {
      margin: 0px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      width: 100px;
      text-align: center;
    }
  }
`;

const TabsRoot = styled.div`
  flex-grow: 1;
  background-color: #f5f5f5;
  margin-top: 20px;
  display: flex;
  min-height: 224px;
`;

const ImportParentGrid = styled(Grid)`
  align-items: center;
  justify-content: space-between;
`;

const ImportPackDiv = styled.div`
    color:blue;
    cursor:pointer;
    border-radius: 6px; 
    padding: 10px;
    &:hover {
      background-color: white;
      box-shadow: 0px 1px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 1px 0px rgba(0, 0, 0, 0.12);
    }
`;

const MainDiv = styled.div`
  min-height: 100vh;
  background: #f5f5f5;
  flex-grow: 1;
  overflow: hidden;
`;

const NewPackMenuItemStyled = styled(MenuItem)`
  && {
    width: 110px;
    /* margin-left: auto; */
    margin-top: 20px;
  }
`;

const CenterAlignedDiv = styled.div`
  max-width: 1250px;
  margin: auto;
`;

const StyledSearch = styled(Search)`
  /* width: 200px; */
  margin-bottom: 20px;
`;

const PackOptionsIconButton = styled(IconButton)`
  && {
    bottom: 23px;
    right: 8px;
    position: absolute !important;
    float: right;
  }
`;

const UserMenuDiv = styled.div`
  text-align: center;
  position: absolute;
  right: 0px;
  // top: 10px;
`;
export default inject(({ store: appStore }) => {
  return {
    store: appStore.leftHoverMenuStore,
    assistantStore: appStore.assistantStore,
    onBoardingStore: appStore.onBoardingStore,
    userStore: appStore.userStore,
  };
})(observer(Dashboard));
